import { shortSpinId } from '../../../Model/Spin/spinId.js'
import React, { useMemo, useState } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import type { SpinMember } from '../../../Generated/SpinMember.ts'
import type { TaxonomyLadder } from '../../../Generated/TaxonomyLadder.ts'
import { Card } from '../AdminStatsPage/StatsUi.js'
import { LastBlasted } from '../SpinPage/LastBlasted.js'
import { SpinUuidLookup } from '../SpinPage/SpinUuidLookup.js'
import { SpinEvidenceCard } from './SpinEvidenceCard.js'
import { InfoTip } from './SpinUi.js'
import { windowNote } from './spinMetrics.logic.js'
import { num, parseRefKind, pct, refDetailPath, refKindLabel } from './spinRefDetail.logic.js'
import { useSpinDetail } from './useSpinData.js'

const RANK_LABELS = ['Domain', 'Kingdom', 'Phylum', 'Class', 'Order', 'Family', 'Genus', 'Species']

// NB Micronaut omits empty collections, so `taxonomies` arrives as `undefined` (not `[]`) for members
// with no local taxonomy (foreign OTUs) — hence the null-guards throughout this file.
const uniqueSources = (ladders: TaxonomyLadder[]): string[] =>
  [...new Set(ladders.filter((l) => l != null).map((l) => l.source))].sort()

/** Pick a member's ladder for a source, preferring the top hit (lowest hitRank). */
const ladderFor = (member: SpinMember, source: string): TaxonomyLadder | undefined =>
  (member.taxonomies ?? []).filter((t) => t.source === source).sort((a, b) => a.hitRank - b.hitRank)[0]

const altLadderFor = (member: SpinMember, source: string): TaxonomyLadder | undefined =>
  (member.taxonomies ?? []).find((t) => t.source === source && t.hitRank === 1)

const spark = (v: number | undefined): string => (v == null ? '—' : v.toFixed(3))

// Columns before the Spark column in the member table; the alt-hit sub-row spans them with its label.
const MEMBER_LEAD_COLUMNS = 8

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm break-all">{children ?? '—'}</div>
  </div>
)

export const SpinDetailPage = () => {
  const user = useGetAuthenticatedUser()
  const { spinId } = useParams<{ spinId: string }>()
  const { detail, loading, error } = useSpinDetail(spinId)
  // Carry the run/taxonomy selection into the reference drill-down links, like the other SPIN pages.
  const [params] = useSearchParams()
  const qs = params.toString()
  const suffix = qs ? `?${qs}` : ''

  const spinTaxSources = useMemo(() => uniqueSources(detail?.taxonomies ?? []), [detail])
  const memberTaxSources = useMemo(
    () => uniqueSources((detail?.members ?? []).flatMap((m) => m.taxonomies ?? [])),
    [detail]
  )
  const [spinTaxSource, setSpinTaxSource] = useState('')
  const [memberTaxSource, setMemberTaxSource] = useState('')
  const [showAltHits, setShowAltHits] = useState(true)

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  const effSpinSource = spinTaxSources.includes(spinTaxSource) ? spinTaxSource : (spinTaxSources[0] ?? '')
  const effMemberSource = memberTaxSources.includes(memberTaxSource) ? memberTaxSource : (memberTaxSources[0] ?? '')
  const spinTaxRows = (detail?.taxonomies ?? []).filter((t) => t.source === effSpinSource)
  const scoreSources = [
    ...new Set([...Object.keys(detail?.unityBySource ?? {}), ...Object.keys(detail?.reachBySource ?? {})])
  ].sort()

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">SPIN detail</h1>
        <div className="flex flex-wrap items-center gap-4">
          <SpinUuidLookup toPath={(id) => `/admin/spin/s/${id}${suffix}`} />
          <Link to="/admin/spin" className="link link-primary text-sm">
            ← Back to metrics
          </Link>
        </div>
      </header>

      {loading && <p className="text-gray-500">Loading…</p>}
      {error && <p className="text-error">Could not load SPIN: {error}</p>}

      {!loading && !error && detail && (
        <>
          <Card title="Core">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Field label="SPIN id">
                <span className="font-mono">{detail.core.id}</span>
              </Field>
              <Field label="Amplicon">{detail.core.amplicon}</Field>
              <Field label="Algorithm">
                {detail.core.algorithmType} · {detail.core.algorithmVersion}
              </Field>
              <Field label="Kind">{detail.core.kind ?? '—'}</Field>
              <Field label="Minted at">{detail.core.mintedAt}</Field>
              <Field label="Last blasted">
                <LastBlasted at={detail.core.lastBlastedAt} source={detail.core.lastBlastSource} />
              </Field>
              <Field label="Reason">{detail.core.reason}</Field>
              <Field label="Members">{detail.core.memberCount.toLocaleString()}</Field>
              <Field label="Owner OTU">
                <span className="font-mono">{detail.core.ownerOtuUuid ?? '—'}</span>
              </Field>
              <Field label="Minted run">
                {detail.core.mintedRunId ? (
                  <span className="font-mono text-xs">{detail.core.mintedRunId.slice(0, 8)}</span>
                ) : (
                  '—'
                )}
              </Field>
              <Field label="Identity hash">
                <span className="font-mono text-xs break-all">{detail.core.metadataHash ?? '—'}</span>
                {detail.core.hashVersion && <span className="text-gray-400"> (v{detail.core.hashVersion})</span>}
              </Field>
              <Field label="Created at">{detail.core.createdAt}</Field>
              <Field label="Predecessors">
                {(detail.core.predecessorSpinIds ?? []).length === 0 ? (
                  '—'
                ) : (
                  <div className="flex flex-col">
                    {(detail.core.predecessorSpinIds ?? []).map((p) => (
                      <Link key={p} to={`/admin/spin/s/${p}`} className="link link-primary font-mono text-xs">
                        {shortSpinId(p)}
                      </Link>
                    ))}
                  </div>
                )}
              </Field>
            </div>
            {detail.core.coreSequence && (
              <div className="mt-4">
                <div className="text-xs text-gray-500">Core sequence</div>
                <div className="font-mono text-xs break-all bg-gray-50 rounded p-2 max-h-32 overflow-auto">
                  {detail.core.coreSequence}
                </div>
              </div>
            )}
            {detail.core.spinAlgorithmData && detail.core.spinAlgorithmData !== '{}' && (
              <div className="mt-4">
                <div className="text-xs text-gray-500">Algorithm data</div>
                <pre className="font-mono text-xs break-all bg-gray-50 rounded p-2 max-h-32 overflow-auto">
                  {detail.core.spinAlgorithmData}
                </pre>
              </div>
            )}
          </Card>

          <SpinEvidenceCard evidence={detail.core.evidence} />

          <Card title="Scores (per source)">
            {scoreSources.length === 0 ? (
              <p className="text-gray-500 text-sm">No scores — members have no local taxonomy.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-xs">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th className="text-right">Unity</th>
                      <th className="text-right">Reach</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoreSources.map((src) => (
                      <tr key={src}>
                        <td>{src}</td>
                        <td className="text-right">{(detail.unityBySource ?? {})[src]?.toFixed(3) ?? '—'}</td>
                        <td className="text-right">{(detail.reachBySource ?? {})[src]?.toFixed(3) ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="Resolved SPIN taxonomy">
            {spinTaxSources.length === 0 ? (
              <p className="text-gray-500 text-sm">No resolved taxonomy for this SPIN.</p>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm mb-2">
                  <span className="text-gray-500">Source</span>
                  <select
                    className="select select-bordered select-xs"
                    value={effSpinSource}
                    onChange={(e) => setSpinTaxSource(e.target.value)}
                  >
                    {spinTaxSources.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="overflow-x-auto">
                  <table className="table table-xs">
                    <thead>
                      <tr>
                        <th>Hit rank</th>
                        {RANK_LABELS.map((r) => (
                          <th key={r}>{r}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {spinTaxRows.map((t) => (
                        <tr key={t.hitRank}>
                          <td>{t.hitRank}</td>
                          {(t.ranks ?? []).map((v, i) => (
                            <td key={i}>{v ?? '—'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          <Card title={`Reference ids in this SPIN (${(detail.referenceIds ?? []).length})`}>
            <p className="text-sm text-gray-600 mb-2">
              What this SPIN lumps together: the distinct NCBI accessions / BOLD bins carried by its current members, most-carried first.
              A count well below the SPIN&apos;s {detail.core.memberCount.toLocaleString()} members means either several references in one
              cluster or members carrying none.
              <InfoTip tip="This roll-up covers ALL current members and is capped at 50 ids, whereas the member table below is capped by the request's memberLimit. The two are NOT in sync: a large SPIN can list reference ids whose members are not in the visible member window." />
            </p>
            {(detail.referenceIds ?? []).length === 0 ? (
              <p className="text-gray-500 text-sm">No current member carries an NCBI accession or BOLD bin.</p>
            ) : (
              <div className="overflow-x-auto max-h-[24rem]">
                <table className="table table-xs table-pin-rows">
                  <thead>
                    <tr>
                      <th>Kind</th>
                      <th>Reference id</th>
                      <th className="text-right">
                        Members carrying it
                        <InfoTip tip="Current members of this SPIN whose blast/BOLD row produced the id — counted over every current member, not only the ones listed below." />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.referenceIds ?? []).map((r) => {
                      const kind = parseRefKind(r.refKind)
                      return (
                        <tr key={`${r.refKind}:${r.refId}`}>
                          <td>{refKindLabel(r.refKind)}</td>
                          <td className="font-mono break-all">
                            {kind ? (
                              <Link to={refDetailPath(kind, r.refId, suffix)} className="link link-primary">
                                {r.refId}
                              </Link>
                            ) : (
                              r.refId
                            )}
                          </td>
                          <td className="text-right tabular-nums">{num(r.memberCount)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title={`Member OTUs (${(detail.members ?? []).length})`}>
            {windowNote((detail.members ?? []).length, detail.core.memberCount, 'current members') && (
              <p className="text-sm text-warning font-medium mb-2">
                {windowNote((detail.members ?? []).length, detail.core.memberCount, 'current members')}
                <InfoTip tip="Only this member list is capped — the reference-id roll-up above still covers every current member, so it can name ids no visible row carries." />
              </p>
            )}
            <div className="flex items-center gap-2 text-sm mb-2">
              <span className="text-gray-500">Taxonomy source</span>
              {memberTaxSources.length === 0 ? (
                <span className="text-gray-500">none available locally</span>
              ) : (
                <select
                  className="select select-bordered select-xs"
                  value={effMemberSource}
                  onChange={(e) => setMemberTaxSource(e.target.value)}
                >
                  {memberTaxSources.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}
              <label className="flex items-center gap-1 ml-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs"
                  checked={showAltHits}
                  onChange={(e) => setShowAltHits(e.target.checked)}
                />
                Show alt hits
                <InfoTip tip="Adds a row under each member with its alternative hit (hit rank 1) for the selected source. Ranks where the alt hit disagrees with the top hit are highlighted." />
              </label>
            </div>
            <div className="overflow-x-auto max-h-[40rem]">
              <table className="table table-xs table-pin-rows">
                <thead>
                  <tr>
                    <th>OTU</th>
                    <th>Rep</th>
                    <th className="text-right">Reads</th>
                    <th>
                      NCBI accession
                      <InfoTip tip="Accession from the member's NCBI blast row, normalised (lowercased) exactly as reference concordance keys on it. Null for a foreign OTU with no local blast row." />
                    </th>
                    <th className="text-right">
                      NCBI identity
                      <InfoTip tip="hit_percentage from that same NCBI blast row — deliberately labelled NCBI, not a generic hit %: a member carrying only a BOLD bin legitimately shows a bin with no identity here." />
                    </th>
                    <th className="text-right">
                      NCBI coverage
                      <InfoTip tip="query_coverage from the same NCBI blast row." />
                    </th>
                    <th>
                      BOLD bin
                      <InfoTip tip="BOLD BIN key extracted from the member's BOLD link — the cross-source identifier. Its own identity lives in the BOLD blast row and is NOT the NCBI identity shown here." />
                    </th>
                    <th>
                      staxids
                      <InfoTip tip="Raw staxids column, display only. It is multi-valued on ~0.26% of rows (e.g. 123;456), whereas the taxon-id drill-down keys on single split ids — so it is deliberately not linked." />
                    </th>
                    <th className="text-right">
                      Spark
                      <InfoTip tip="Spark score of the hit in that row, from the member's blast row for the selected source: spark_top on the member row, spark_alt on its alt-hit row. Sources without a blast row (e.g. Consensus) show —. Sparks are point-in-time evidence that moves with reference-DB releases; see the Evidence card for the date and database versions." />
                    </th>
                    {RANK_LABELS.map((r) => (
                      <th key={r}>{r}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(detail.members ?? []).map((m) => {
                    const ladder = ladderFor(m, effMemberSource)
                    const alt = showAltHits ? altLadderFor(m, effMemberSource) : undefined
                    return (
                      <React.Fragment key={m.otuUuid}>
                        <tr>
                          <td className="font-mono">{m.otuUuid.slice(0, 8)}</td>
                          <td>{m.isRepresentative ? '★' : ''}</td>
                          <td className="text-right tabular-nums">{m.reads.toLocaleString()}</td>
                          <td className="font-mono break-all">{m.ncbiId ?? '—'}</td>
                          <td className="text-right tabular-nums">{pct(m.hitPercentage)}</td>
                          <td className="text-right tabular-nums">{m.queryCoverage == null ? '—' : `${m.queryCoverage}%`}</td>
                          <td className="font-mono break-all">{m.boldBin ?? '—'}</td>
                          <td className="font-mono break-all">{m.staxids ?? '—'}</td>
                          <td className="text-right tabular-nums">{spark(m.sparkTopBySource?.[effMemberSource])}</td>
                          {RANK_LABELS.map((_, i) => (
                            <td key={i}>{ladder?.ranks?.[i] ?? '—'}</td>
                          ))}
                        </tr>
                        {alt && (
                          <tr className="text-gray-500 bg-base-200/50">
                            <td colSpan={MEMBER_LEAD_COLUMNS} className="text-right italic">
                              ↳ alt hit
                            </td>
                            <td className="text-right tabular-nums">{spark(m.sparkAltBySource?.[effMemberSource])}</td>
                            {RANK_LABELS.map((_, i) => {
                              const v = alt.ranks?.[i] ?? null
                              const differs = v !== (ladder?.ranks?.[i] ?? null)
                              return (
                                <td key={i} className={differs ? 'text-warning font-semibold' : undefined}>
                                  {v ?? '—'}
                                </td>
                              )
                            })}
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
